import type { EventCapturer } from '#src/analytics/analytics_event.ts';
import { GroupNameDrawEvent } from '#src/groups/analytics/group_analytics_events.ts';
import { DisplayParticipantName } from '#src/ui/presentation/display_participant_name.tsx';
import { Button } from '@packages/ui/base/button/button';
import { Card } from '@packages/ui/base/card/card';
import confetti from 'canvas-confetti';
import { useEffect, useRef, useState } from 'react';

/** The rate at which the name on the screen changes during drawing animation. */
const NAME_CYCLE_INTERVAL_MS = 160;
/** Minimum animation duration regardless of participant count. */
const MIN_ANIMATION_DURATION_MS = 2000;
/** Maximum animation duration regardless of participant count. */
const MAX_ANIMATION_DURATION_MS = 4000;
/** Milliseconds of animation added per eligible participant. */
const MS_PER_PARTICIPANT = 400;

export interface DrawSlotMachineProps {
  captureEvent: EventCapturer;
  /** Names to cycle through in the slot machine animation (should exclude current participant). */
  names: string[];
  /** The existing assignment. The animation never changes who the participant is matched with. */
  matchName: string;
  /** Called once when the match is displayed, which counts as a reveal on this device. */
  onReveal: () => void;
  onDrawDone: () => void;
}

export const DrawSlotMachine = ({
  captureEvent,
  names,
  matchName,
  onReveal,
  onDrawDone,
}: DrawSlotMachineProps) => {
  // Stable refs so the setTimeout callback always uses the latest callbacks
  // without adding them to the effect dependencies, which would restart the animation.
  const captureEventRef = useRef(captureEvent);
  captureEventRef.current = captureEvent;
  const onRevealRef = useRef(onReveal);
  onRevealRef.current = onReveal;

  // Scale duration with group size so small draws feel snappy and large ones get enough drama.
  // Captured in a ref so the effect runs exactly once on mount (names is stable after mount).
  const animationDurationRef = useRef(
    Math.min(
      MAX_ANIMATION_DURATION_MS,
      Math.max(MIN_ANIMATION_DURATION_MS, names.length * MS_PER_PARTICIPANT),
    ),
  );

  const shootConfetti = () => {
    confetti({
      particleCount: 100,
      origin: {
        x: 0.5,
        y: 0.3,
      },
    });
  };

  const endAnimationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showMatch, setShowMatch] = useState(false);

  useEffect(() => {
    // Start the animation
    if (!endAnimationTimeoutRef.current) {
      endAnimationTimeoutRef.current = setTimeout(() => {
        // Track success here so we confirm the user actually saw their match.
        captureEventRef.current(GroupNameDrawEvent, {
          isSuccess: true,
          failureReason: null,
        });
        onRevealRef.current();
        setShowMatch(true);
        shootConfetti();
      }, animationDurationRef.current);
    }
    // Clear and stop the animation
    return () => {
      if (endAnimationTimeoutRef.current) {
        clearTimeout(endAnimationTimeoutRef.current);
        endAnimationTimeoutRef.current = null;
      }
      setShowMatch(false);
    };
  }, []);

  return (
    <Card>
      <div className="animate-in fade-in flex min-h-0 flex-1 flex-col text-center">
        <div className="flex flex-col gap-20">
          <div>{showMatch ? 'Your match is...' : 'Reaching into the hat...'}</div>

          {showMatch ? (
            <SlotMachineWinner name={matchName} />
          ) : (
            <NameSlotMachine names={names} cycleIntervalMs={NAME_CYCLE_INTERVAL_MS} />
          )}

          {/* Keep div to maintain 3 flex col layout */}
          <div>
            {showMatch && (
              <Button variant="primary" className="w-full" onClick={onDrawDone}>
                <span>Done</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

interface NameSlotMachineProps {
  names: string[];
  cycleIntervalMs: number;
}

const NameSlotMachine = ({ names, cycleIntervalMs }: NameSlotMachineProps) => {
  const [shownIndex, setShownIndex] = useState<number>(0);
  const cycleNamesIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Start the name cycling
    cycleNamesIntervalRef.current = setInterval(() => {
      setShownIndex(current => (current + 1) % names.length);
    }, cycleIntervalMs);

    // Clear the name cycling
    return () => {
      if (cycleNamesIntervalRef.current) {
        clearInterval(cycleNamesIntervalRef.current);
        cycleNamesIntervalRef.current = null;
      }
    };
  }, [names.length, cycleIntervalMs]);

  const currentName = names[shownIndex];
  if (!currentName) {
    throw new Error('No name found');
  }

  return <DisplayParticipantName name={currentName} className="animate-pulse text-4xl font-bold" />;
};

const SlotMachineWinner = ({ name }: { name: string }) => {
  return <DisplayParticipantName name={name} className="text-4xl font-bold" />;
};
